import * as React from 'react';
import {
  StackLayout,
  StackItem,
  Button,
  Modal,
  HeadingText,
  Box,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { isUsingConfigAppBackend } from '@web-config-app/common';
import {
  EntityDetail,
  getEntityInstanceName,
  getDomainEntityId,
  findEntityModulePath,
  hasEntityOperation,
} from '@web-config-app/core';
import {
  useEntityList,
  useConfigApp,
  useConfigAppPath,
} from '@web-config-app/core-react';
import {
  SelectableEntityListTable,
  EntityListTablePagination,
  SearchForm,
  EmptySearchCard,
  EmptyEntityListCard,
  EntityName,
} from '@web-config-app/core-react-ui';
import { openInTab } from '@leagueplatform/web-common';
import { useEntityDetailsProps } from '../../hooks/use-entity-details-props/use-entity-details-props';
import type { EntityListSelectModalProps } from '../../types/components';

export const AddEntityReferenceFromListModal: React.FC<
  EntityListSelectModalProps
> = ({
  onSubmit,
  onCancel,
  open,
  domainEntity,
  headingLabel,
  parentEntityId,
  isRelationshipReference,
  domainEntityAttributesQuery,
}) => {
  const { formatMessage } = useIntl();
  const [selectedEntity, setSelectedEntity] = React.useState<
    EntityDetail | undefined
  >();
  const { currentAuthoringEnvironment } = useEntityDetailsProps();
  const isConfigAppBackendEnabled = isUsingConfigAppBackend({
    entityLevelOverride: domainEntity.isUsingConfigAppBackend,
  });
  const { modules = [], entityConfigs } = useConfigApp();
  const generateConfigPaths = useConfigAppPath();
  const entityModulePath = findEntityModulePath(
    modules,
    domainEntity.type,
    entityConfigs,
  );
  const { entityCreatePath } = generateConfigPaths({
    tabId: entityModulePath?.tabId,
    moduleId: entityModulePath?.moduleId,
    childModuleId: entityModulePath?.childModuleId,
    environmentKey: currentAuthoringEnvironment,
  });
  /**
   * If this is just a reference, and not a relationship, we must filter by published status since
   * the entity id value that is stored on the property is the domainEntityId, and not the CACT entity ID.
   * The DomainEntityId will only exist on published entities.
   */
  const entityStatusFilter =
    !isRelationshipReference && isConfigAppBackendEnabled
      ? 'published'
      : undefined;
  /**
   * It would be maybe preferable for this modal to not be consuming useEntityList directly and instead
   * implementing some API (from `useEntityDetailsProps()?) to be able to get this data.
   */
  const {
    pagination,
    data,
    isLoading,
    loadPage,
    searchQuery,
    onSearch,
    isError,
  } = useEntityList({
    entity: domainEntity,
    enabled: open,
    environment: currentAuthoringEnvironment,
    domainEntityType: isConfigAppBackendEnabled ? domainEntity.type : undefined,
    domainEntityAttributesQuery: domainEntityAttributesQuery ?? undefined,
    entityStatusFilter,
  });

  const noSearchResults = Boolean(
    data && data.length === 0 && !isLoading && searchQuery && !isError,
  );

  const emptyDataDescription = formatMessage(
    {
      id: entityStatusFilter
        ? 'EMPTY_SELECTABLE_TABLE_PUBLISHED_ENTITY_DESCRIPTION'
        : 'EMPTY_SELECTABLE_TABLE_ENTITY_DESCRIPTION',
    },
    { entity: formatMessage({ id: domainEntity.labelKey }, { count: 2 }) },
  );

  const emptyDataResults =
    data && data.length === 0 && !isLoading && !searchQuery;

  const showResultsTable = isLoading || (data && data?.length > 0);

  const showSearchBar = isConfigAppBackendEnabled && !emptyDataResults;
  const doneButtonAction = () => {
    if (selectedEntity) {
      onSubmit(selectedEntity);
    } else {
      onCancel();
    }
  };

  return (
    <Modal.Root
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) {
          onCancel();
          // Clear search parameters on close to prevent them from persisting
          onSearch('');
        }
      }}
      open={open}
    >
      <Modal.Content
        layout="fullscreen"
        css={{
          '.GDS-modal-content': {
            padding: '$none',
          },
        }}
      >
        <StackLayout
          spacing="$oneAndHalf"
          horizontalAlignment="stretch"
          css={{ padding: '$oneAndHalf', flexGrow: 1 }}
        >
          <StackLayout
            orientation="horizontal"
            horizontalAlignment="stretch"
            verticalAlignment="center"
            css={{ flexGrow: 0 }}
          >
            <StackItem horizontalAlignment="fillContainer">
              <Modal.Title>
                <HeadingText level="2" size="lg">
                  {headingLabel}
                </HeadingText>
              </Modal.Title>
            </StackItem>

            {hasEntityOperation(domainEntity, 'create') && (
              <StackItem horizontalAlignment="end">
                <Button
                  size="medium"
                  priority="secondary"
                  icon="interfacePlus"
                  onClick={() => openInTab(entityCreatePath)}
                >
                  {formatMessage(
                    { id: 'CREATE_A_NEW_ENTITY' },
                    {
                      entityName: (
                        <EntityName id={domainEntity.labelKey} singular />
                      ),
                    },
                  )}
                </Button>
              </StackItem>
            )}
          </StackLayout>
          {showSearchBar && (
            <SearchForm
              id="selectable-entity-list-search"
              label={formatMessage(
                { id: 'ENTITY_SEARCH' },
                {
                  entityName: formatMessage(
                    { id: domainEntity.labelKey },
                    { count: 2 },
                  ),
                },
              )}
              onSearchSubmit={onSearch}
            />
          )}
          {emptyDataResults && !isError && (
            <StackItem
              css={{
                flexGrow: 1,
                display: 'flex',
                '> *': { flexGrow: 1 },
              }}
            >
              <EmptyEntityListCard
                heading={formatMessage(
                  { id: 'EMPTY_ENTITY_TITLE' },
                  {
                    entity: formatMessage(
                      { id: domainEntity.labelKey },
                      { count: 2 },
                    ),
                  },
                )}
                description={emptyDataDescription}
              />
            </StackItem>
          )}
          {isError && (
            <EmptyEntityListCard
              heading={formatMessage(
                { id: 'ERROR_ENTITY_TITLE' },
                {
                  entity: formatMessage(
                    { id: domainEntity.labelKey },
                    { count: 2 },
                  ),
                },
              )}
              description={formatMessage(
                { id: 'ERROR_ENTITY_DESCRIPTION' },
                {
                  entity: formatMessage(
                    { id: domainEntity.labelKey },
                    { count: 2 },
                  ),
                },
              )}
            />
          )}
          {noSearchResults && !isError && <EmptySearchCard />}

          {showResultsTable && (
            <SelectableEntityListTable
              getDomainEntityId={(d: EntityDetail) =>
                getDomainEntityId(d, isConfigAppBackendEnabled)
              }
              entityData={data}
              isLoading={isLoading}
              parentEntityId={parentEntityId}
              getItemName={(d: EntityDetail) =>
                getEntityInstanceName(d, domainEntity)
              }
              selectionProps={{
                onEntitySelected: (e: EntityDetail) => {
                  setSelectedEntity(e);
                },
              }}
              domainEntity={domainEntity}
            />
          )}
          {pagination && data && data.length > 0 && (
            <Box>
              <EntityListTablePagination
                pagination={pagination}
                loadPage={loadPage}
              />
            </Box>
          )}
        </StackLayout>
        <StackLayout
          orientation="horizontal"
          horizontalAlignment="end"
          css={{
            padding: '$one $oneAndHalf',
            position: 'sticky',
            insetBlockEnd: 0,
            flexGrow: 0,
            backgroundColor: '$surfaceBackgroundSecondary',
            borderTop:
              'solid $colors$onSurfaceBorderSubdued $borderWidths$thin',
          }}
        >
          <Button size="medium" priority="primary" onClick={doneButtonAction}>
            {formatMessage({ id: 'DONE' })}
          </Button>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
